/**
 * DRE Controls Panel
 *
 * Shared component for DRE dashboard and drill-down pages.
 * Provides consistent filtering controls styled for Monutrack dark theme:
 * - Período (Period preset buttons)
 * - Intervalo (Date range inputs)
 * - Comparar (Comparison mode selector)
 * - Agrupar por (Granularity segmented control)
 * - PROA (Budget comparison selector)
 * - Salvar (Filter presets management)
 */

import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { PeriodPresetSelector, type PeriodDates } from './PeriodPresetSelector';
import {
  ComparisonSelector,
  calculateComparisonRange,
  type ComparisonMode,
} from './ComparisonSelector';
import { ProaComparisonSelector, type ProaComparisonState } from './ProaComparisonSelector';
import { FilterPresetManager } from './FilterPresetManager';
import type { FilterPreset, FilterPresetFilters } from '../services/filterPresetService';

// Granularity type - matches DRE page
export type DREGranularity = 'accumulated' | 'yearly' | 'quarterly' | 'monthly';

const GRANULARITY_OPTIONS: { value: DREGranularity; label: string }[] = [
  { value: 'accumulated', label: 'Acumulado' },
  { value: 'yearly', label: 'Por Ano' },
  { value: 'quarterly', label: 'Por Trimestre' },
  { value: 'monthly', label: 'Por Mês' },
];

// ============================================================================
// SEGMENTED CONTROL (Dark Theme)
// ============================================================================

interface SegmentedControlProps<T extends string> {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: SegmentedControlProps<T>) {
  return (
    <div className="inline-flex rounded-lg border border-brand-dark-lighter overflow-hidden bg-brand-dark-lighter">
      {options.map((option, index) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`
              segmented-control-button px-3 py-1.5 text-xs font-medium transition-all duration-150
              focus:outline-none focus:ring-2 focus:ring-brand-primary focus:z-10
              ${index > 0 ? 'border-l border-brand-dark-light' : ''}
              ${
                isActive
                  ? 'bg-brand-primary text-brand-dark-deepest font-semibold'
                  : 'bg-transparent text-brand-gray hover:bg-brand-dark-light hover:text-brand-primary'
              }
            `}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// CONTROLS PANEL
// ============================================================================

export interface DREControlsPanelProps {
  // Period
  startDate: string;
  endDate: string;
  onPeriodChange: (dates: PeriodDates) => void;

  // Interval (date inputs)
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  showRefreshButton?: boolean;

  // Comparison
  comparisonMode: ComparisonMode;
  onComparisonModeChange: (mode: ComparisonMode) => void;
  comparisonRange?: { startDate: string; endDate: string } | null;

  // Granularity
  granularity: DREGranularity;
  onGranularityChange: (granularity: DREGranularity) => void;

  // Row visibility
  showPeriod?: boolean;
  showInterval?: boolean;
  showComparison?: boolean;
  showGranularity?: boolean;
  showProaComparison?: boolean;

  // PROA Comparison (optional)
  proaComparison?: ProaComparisonState;
  onProaComparisonChange?: (state: ProaComparisonState) => void;

  // Filter Presets (optional)
  showPresets?: boolean;
  presets?: FilterPreset[];
  onSavePreset?: () => void;
  onApplyPreset?: (preset: FilterPreset) => void;
  onDeletePreset?: (presetId: string) => void;
}

export function DREControlsPanel({
  startDate,
  endDate,
  onPeriodChange,
  onStartDateChange,
  onEndDateChange,
  onRefresh,
  isLoading = false,
  showRefreshButton = true,
  comparisonMode,
  onComparisonModeChange,
  comparisonRange,
  granularity,
  onGranularityChange,
  showPeriod = true,
  showInterval = true,
  showComparison = true,
  showGranularity = true,
  showProaComparison = true,
  proaComparison,
  onProaComparisonChange,
  showPresets = true,
  presets = [],
  onSavePreset,
  onApplyPreset,
  onDeletePreset,
}: DREControlsPanelProps) {
  // Calculate comparison range if not provided
  const effectiveComparisonRange =
    comparisonRange ?? calculateComparisonRange(startDate, endDate, comparisonMode);

  // Build current filters object for preset manager
  const currentFilters: FilterPresetFilters = {
    startDate,
    endDate,
    granularity,
    comparisonMode,
  };

  // Determine if presets should be shown (requires callbacks)
  const presetsEnabled = showPresets && onSavePreset && onApplyPreset && onDeletePreset;

  const [expanded, setExpanded] = useState(false);

  // Local state for date inputs to avoid navigation on every keystroke
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);

  // Sync local state when props change (e.g. preset selection)
  useEffect(() => { setLocalStartDate(startDate) }, [startDate]);
  useEffect(() => { setLocalEndDate(endDate) }, [endDate]);

  const commitStartDate = (date: string) => {
    if (!date || date === startDate) return;
    if (onStartDateChange) {
      onStartDateChange(date);
    } else {
      onPeriodChange({ startDate: date, endDate });
    }
  };

  const commitEndDate = (date: string) => {
    if (!date || date === endDate) return;
    if (onEndDateChange) {
      onEndDateChange(date);
    } else {
      onPeriodChange({ startDate, endDate: date });
    }
  };

  // Check if there are expandable rows
  const hasExpandableRows = showInterval || showComparison || showGranularity || showProaComparison || presetsEnabled;

  return (
    <div className="border border-brand-dark-lighter rounded-xl mb-6 overflow-hidden">
      {/* Primary row: Período + Expand toggle */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4 flex-wrap flex-1">
          {showPeriod && (
            <>
              <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
                Período
              </span>
              <PeriodPresetSelector
                startDate={startDate}
                endDate={endDate}
                onPeriodChange={onPeriodChange}
              />
            </>
          )}
        </div>

        {hasExpandableRows && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 text-xs text-brand-gray hover:text-white transition-colors ml-4 flex-shrink-0"
          >
            <span>{expanded ? 'Recolher filtros' : 'Expandir filtros'}</span>
            <ChevronDown
              size={14}
              className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {/* Expandable rows with animation */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 px-4 pb-4 border-t border-brand-dark-lighter pt-4">
            {/* INTERVALO Row */}
            {showInterval && (
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
                  Intervalo
                </span>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={localStartDate}
                    onChange={(e) => {
                      setLocalStartDate(e.target.value);
                      commitStartDate(e.target.value);
                    }}
                    className="dre-date-input bg-brand-dark-lighter border border-brand-dark-lighter rounded-lg px-3 py-1.5 text-xs text-white
                               focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  />
                  <span className="text-brand-gray">→</span>
                  <input
                    type="date"
                    value={localEndDate}
                    onChange={(e) => {
                      setLocalEndDate(e.target.value);
                      commitEndDate(e.target.value);
                    }}
                    className="dre-date-input bg-brand-dark-lighter border border-brand-dark-lighter rounded-lg px-3 py-1.5 text-xs text-white
                               focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  />
                  {showRefreshButton && onRefresh && (
                    <button
                      onClick={onRefresh}
                      disabled={isLoading}
                      className={`
                        px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150
                        focus:outline-none focus:ring-2 focus:ring-brand-primary
                        ${
                          isLoading
                            ? 'bg-brand-dark-lighter text-brand-gray cursor-not-allowed'
                            : 'bg-brand-primary text-brand-dark-deepest hover:bg-brand-accent-600 cursor-pointer'
                        }
                      `}
                    >
                      {isLoading ? 'Carregando...' : 'Atualizar'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* COMPARAR Row */}
            {showComparison && (
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
                  Comparar
                </span>
                <ComparisonSelector
                  value={comparisonMode}
                  onChange={onComparisonModeChange}
                  disabled={isLoading}
                />
                {effectiveComparisonRange && (
                  <span className="text-xs text-brand-gray">
                    ({effectiveComparisonRange.startDate} → {effectiveComparisonRange.endDate})
                  </span>
                )}
              </div>
            )}

            {/* PROA Row */}
            {showProaComparison && proaComparison && onProaComparisonChange && (
              <div className="flex items-start gap-4 flex-wrap">
                <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px] pt-2">
                  PROA
                </span>
                <ProaComparisonSelector
                  year={parseInt(startDate.substring(0, 4), 10)}
                  value={proaComparison}
                  onChange={onProaComparisonChange}
                  disabled={isLoading}
                />
              </div>
            )}

            {/* AGRUPAR POR Row */}
            {showGranularity && (
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
                  Agrupar por
                </span>
                <SegmentedControl
                  options={proaComparison?.enabled ? GRANULARITY_OPTIONS.filter(o => o.value === 'accumulated') : GRANULARITY_OPTIONS}
                  value={proaComparison?.enabled ? 'accumulated' : granularity}
                  onChange={onGranularityChange}
                />
                {proaComparison?.enabled && (
                  <span className="text-xs text-brand-gray italic">
                    (PROA só disponível em Acumulado)
                  </span>
                )}
              </div>
            )}

            {/* SALVAR Row */}
            {presetsEnabled && (
              <div className="flex items-center gap-4 flex-wrap border-t border-brand-dark-lighter pt-4 mt-2">
                <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
                  Salvar
                </span>
                <FilterPresetManager
                  presets={presets}
                  currentFilters={currentFilters}
                  onApplyPreset={onApplyPreset}
                  onSaveClick={onSavePreset}
                  onDeletePreset={onDeletePreset}
                  disabled={isLoading}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DREControlsPanel;
